import React, { useEffect, useState, useMemo, useRef } from 'react';
import CoffeeCardWeb from './CoffeeCardWeb';
import CoffeeCardContainer, { AddCoffeeForm } from './CoffeeCardContainer';
import CoffeeSubmissionTracker from './CoffeeSubmissionTracker';
import CoffeeStatusIndicator from './CoffeeStatusIndicator';
import { url } from '../../api';
import { apiClient } from '../../api/client';
import { ImageCacheProvider } from '../../context/ImageCacheContext';
import { useAppSelector } from '../../store/hooks';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faChevronDown, faPlus, faMapMarkedAlt, faLink, faSearch } from '@fortawesome/free-solid-svg-icons';
import './coffeePage.css';
import { Button, buttonVariants } from '../../ui/components/Button';
import { cn } from '../../ui/utils/cn';
import { getAuthToken } from '../../utils/authTokens';
import { coffeeRatingPaths } from './api/paths';

interface Tweet {
    tweetId: string;
    tweetUrl: string;
    datePosted: Date;
    tweetReplys?: Tweet[];
}

interface CoffeeRateInterface {
    _id: string;
    location: {
        storeCoordinates: { lat: number; lng: number };
        measurementCoordinates: { lat: number; lng: number };
        city: string;
        country: string;
    };
    coffeeType: string;
    coffeeShop: string;
    folderPath: string;
    imgName: string;
    overallRating: number;
    rating: { [key: string]: string };
    format: { [key: string]: string };
    rateColor: string;
    tweet?: Tweet;
    isSelfMade?: boolean;
    createdAt?: string; // using mongoose's built-in timestamp
    updatedAt?: string; // using mongoose's built-in timestamp
    description?: string;
    user?: {
        username: string;
        userEmoji?: string;
    };
}

type SortOption = 'date' | 'rating' | 'style' | 'group-type';

interface CoffeeTypeGroup {
    title: string;
    types: string[];
}

const COFFEE_GROUPS: CoffeeTypeGroup[] = [
    {
        title: 'Hot Coffees',
        types: ['espresso', 'cappuccino', 'filter', 'latte', 'americano', 'flat-white', 'mocha', 'macchiato']
    },
    {
        title: 'Cold Coffees',
        types: ['iced-latte', 'freddo-espresso', 'freddo-cappuccino', 'cold-brew', 'iced-americano']
    },
    {
        title: 'Greek Style',
        types: ['greek-coffee', 'frappe', 'frappe-sketo']
    }
];

const getCoffeeGroup = (coffeeType: string): string => {
    const normalizedType = coffeeType.toLowerCase().replace(/\s+/g, '-');
    for (const group of COFFEE_GROUPS) {
        if (group.types.some(type => normalizedType.includes(type))) {
            return group.title;
        }
    }
    return 'Other Coffees';
};

const ITEMS_PER_PAGE = 24;
const MOBILE_ITEMS_PER_PAGE = 12;
const PRELOAD_THRESHOLD = 0.5;

const CoffeeRatingsPage: React.FC = () => {
    const [rawCoffees, setRawCoffees] = useState<CoffeeRateInterface[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [sortBy, setSortBy] = useState<SortOption>('date');
    const [isMobile, setIsMobile] = useState(false);
    const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
    const [showDeleteButtons, setShowDeleteButtons] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [shouldAutoResume, setShouldAutoResume] = useState(false);
    const [externalSubmissionData, setExternalSubmissionData] = useState<any>(null);
    const [showExternalStatus, setShowExternalStatus] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadingMoreRef = useRef(false);
    const totalCountRef = useRef(0);
    const visibleItemsRef = useRef(visibleItems);
    const loadingRef = useRef(loading);
    const user = useAppSelector((state) => state.auth);

    // debounce search input (300ms) so we don't hit the API on every keystroke
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Detect mobile devices
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            // Update initial visible items based on device
            setVisibleItems(window.innerWidth <= 768 ? MOBILE_ITEMS_PER_PAGE : ITEMS_PER_PAGE);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        return () => {
            window.removeEventListener('resize', checkIfMobile);
        };
    }, []);

    // Cleanup effect to remove body class on unmount
    useEffect(() => {
        return () => {
            document.body.classList.remove('external-status-visible');
        };
    }, []);

    const refreshList = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const sortCoffees = (coffees: CoffeeRateInterface[], sortBy: SortOption) => {
        return [...coffees].sort((a, b) => {
            // always prioritize favorites first (if authenticated and favorites exist)
            const aIsFavorite = (a as any).isFavoriteType || false;
            const bIsFavorite = (b as any).isFavoriteType || false;

            if (aIsFavorite !== bIsFavorite) {
                return aIsFavorite ? -1 : 1; // favorites come first
            }

            // then apply regular sorting
            switch (sortBy) {
                case 'date':
                    // Sort by createdAt descending, if createdAt is null, sort by _id descending
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    if (dateA === 0 && dateB === 0) {
                        return b._id.localeCompare(a._id); // both null, sort by _id
                    } else if (dateA === 0) {
                        return 1; // a is null, b comes first
                    } else if (dateB === 0) {
                        return -1; // b is null, a comes first
                    }
                    return dateB - dateA;
                case 'rating':
                    return b.overallRating - a.overallRating;
                case 'style':
                    // First sort by group, then by type within group
                    const groupA = getCoffeeGroup(a.coffeeType);
                    const groupB = getCoffeeGroup(b.coffeeType);
                    if (groupA !== groupB) {
                        return groupA.localeCompare(groupB);
                    }
                    return a.coffeeType.localeCompare(b.coffeeType);
                case 'group-type':
                    return a.coffeeType.localeCompare(b.coffeeType);
                default:
                    return 0;
            }
        });
    };

    // when searching, keep API relevance order; otherwise apply sort
    const sortedCoffees = useMemo(() => {
        if (debouncedSearchQuery) return rawCoffees;
        return sortCoffees(rawCoffees, sortBy);
    }, [rawCoffees, sortBy, debouncedSearchQuery]);

    // keep refs in sync for observer callback (avoids stale closures)
    totalCountRef.current = sortedCoffees.length;
    visibleItemsRef.current = visibleItems;
    loadingRef.current = loading;

    useEffect(() => {
        const fetchCoffees = async () => {
            setLoading(true);
            try {
                const token = getAuthToken();
                const params = new URLSearchParams();
                if (debouncedSearchQuery) {
                    params.set('q', debouncedSearchQuery);
                } else {
                    params.set('sortBy', 'createdAt');
                    params.set('sortOrder', 'desc');
                }
                const response = await apiClient.get(`${url}${coffeeRatingPaths.list()}?${params.toString()}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                setRawCoffees(response.data);
            } catch (error) {
                console.error('Error fetching coffees:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCoffees();
    }, [refreshTrigger, debouncedSearchQuery]);

    // reset visible items when search changes so pagination starts from first batch
    useEffect(() => {
        setVisibleItems(isMobile ? MOBILE_ITEMS_PER_PAGE : ITEMS_PER_PAGE);
    }, [debouncedSearchQuery, isMobile]);

    // clear "loading more" guard when visibleItems has updated so next intersection can fire
    useEffect(() => {
        loadingMoreRef.current = false;
    }, [visibleItems]);

    // create a single observer that reads from refs so it never has stale values
    useEffect(() => {
        const batchSize = isMobile ? MOBILE_ITEMS_PER_PAGE * 2 : ITEMS_PER_PAGE * 2;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                if (loadingRef.current || loadingMoreRef.current) return;
                if (visibleItemsRef.current >= totalCountRef.current) return;
                loadingMoreRef.current = true;
                setVisibleItems(prev => Math.min(prev + batchSize, totalCountRef.current));
            },
            { threshold: PRELOAD_THRESHOLD, rootMargin: '1000px 0px' }
        );
        observerRef.current = observer;
        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, [isMobile]);

    // (re-)observe the sentinel whenever we have more to load and the node is in the DOM
    useEffect(() => {
        const hasMore = visibleItems < sortedCoffees.length;
        const el = loadMoreRef.current;
        if (!hasMore || !el || !observerRef.current) return;
        observerRef.current.observe(el);
        return () => {
            observerRef.current?.unobserve(el);
        };
    }, [visibleItems, sortedCoffees.length]);

    // Reset visible items when sort changes, but respect device type
    useEffect(() => {
        setVisibleItems(isMobile ? MOBILE_ITEMS_PER_PAGE : ITEMS_PER_PAGE);
    }, [sortBy, isMobile]);

    const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSortBy(event.target.value as SortOption);
    };

    const handleCoffeeDeleted = () => {
        refreshList();
    };

    // Handle opening the add coffee form
    const handleAddCoffee = () => {
        setShouldAutoResume(false);
        setShowAddForm(true);
    };

    // Handle closing the add coffee form
    const handleCloseForm = () => {
        setShowAddForm(false);
        setShouldAutoResume(false);
    };

    // Handle resuming from status card
    const handleResumeFromStatus = () => {
        setShouldAutoResume(true);
        setShowAddForm(true);
        setShowExternalStatus(false);
        document.body.classList.remove('external-status-visible');
    };

    // Handle successful coffee addition
    const handleAddSuccess = () => {
        setShowAddForm(false);
        setShowExternalStatus(false);
        setExternalSubmissionData(null);
        // Refresh the coffee list
        refreshList();
    };

    // Mirror the form's submission status into the status card. The form also
    // reports `idle` on mount and after a failed submission is reset; nothing is
    // in flight then, so hide the card rather than show "ready, 0%".
    const handleSubmissionUpdate = (submissionData: any) => {
        if (submissionData.status === 'idle') {
            handleDismissStatus();
            return;
        }
        setExternalSubmissionData(submissionData);
        setShowExternalStatus(true);
        // Add body class to adjust layout
        document.body.classList.add('external-status-visible');
    };

    // Handle retry from external status
    const handleRetrySubmission = () => {
        // Reopen the form with the previous data
        setShowAddForm(true);
        setShowExternalStatus(false);
        document.body.classList.remove('external-status-visible');
    };

    // Handle reopen form from external status
    const handleReopenForm = () => {
        setShowAddForm(true);
        setShowExternalStatus(false);
        document.body.classList.remove('external-status-visible');
    };

    // Handle dismiss external status
    const handleDismissStatus = () => {
        setShowExternalStatus(false);
        setExternalSubmissionData(null);
        document.body.classList.remove('external-status-visible');
    };

    const renderGroupedCoffees = () => {
        // Group coffees by their group type
        const groupedCoffees = sortedCoffees.reduce((acc, coffee) => {
            const group = getCoffeeGroup(coffee.coffeeType);
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(coffee);
            return acc;
        }, {} as Record<string, CoffeeRateInterface[]>);

        // Sort groups to maintain consistent order
        const orderedGroups = [
            ...COFFEE_GROUPS.map(g => g.title),
            'Other Coffees'
        ];

        return orderedGroups
            .filter(group => groupedCoffees[group]?.length > 0)
            .map(group => (
                <section key={group} className="coffee-type-section">
                    <div className="coffee-type-header">
                        <h2 className="coffee-type-title">
                            {group}
                            <span className="coffee-type-count">
                                ({groupedCoffees[group].length})
                            </span>
                        </h2>
                    </div>
                    <CoffeeCardContainer
                        coffeeData={groupedCoffees[group].slice(0, visibleItems)}
                        onDelete={handleCoffeeDeleted}
                        showDeleteButtons={showDeleteButtons}
                    />
                </section>
            ));
    };

    const renderGroupedByType = () => {
        // Group coffees by their exact type
        const groupedCoffees = sortedCoffees.reduce((acc, coffee) => {
            const type = coffee.coffeeType;
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push(coffee);
            return acc;
        }, {} as Record<string, CoffeeRateInterface[]>);

        // Format coffee type names for display
        const formatType = (type: string): string => {
            return type
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };

        // Sort by most common types
        return Object.keys(groupedCoffees)
            .sort((a, b) => groupedCoffees[b].length - groupedCoffees[a].length)
            .map(type => (
                <section key={type} className="coffee-type-section">
                    <div className="coffee-type-header">
                        <h2 className="coffee-type-title">
                            {formatType(type)}
                            <span className="coffee-type-count">
                                ({groupedCoffees[type].length})
                            </span>
                        </h2>
                    </div>
                    <CoffeeCardContainer
                        coffeeData={groupedCoffees[type].slice(0, visibleItems)}
                        onDelete={handleCoffeeDeleted}
                        showDeleteButtons={showDeleteButtons}
                    />
                </section>
            ));
    };

    return (
        <ImageCacheProvider>
            {/* Coffee Submission Tracker for pending submissions */}
            <CoffeeSubmissionTracker />

            <div className="coffee-page">
                <div className="coffee-page-header">
                    <div className="page-header-left">
                        <h1 className="page-title">Coffee Ratings</h1>

                        {/* Primary actions — icon + readable label (avoid icon-only mystery meat) */}
                        <div className="coffee-header-actions">
                            {user && user.token && (
                                <Button
                                    mobileSlot="special1"
                                    variant="outline"
                                    size="sm"
                                    icon={faPlus}
                                    onClick={handleAddCoffee}
                                    aria-label="Add a new coffee rating"
                                    title="Submit a tasting with photo, café, and score"
                                    className="coffee-header-action-btn shrink-0"
                                    type="button"
                                >
                                    Add rating
                                </Button>
                            )}

                            {user.role === 'admin' && (
                                <>
                                    <Button
                                        type="button"
                                        variant={showDeleteButtons ? 'destructive' : 'outline'}
                                        size="sm"
                                        icon={showDeleteButtons ? faEyeSlash : faEye}
                                        aria-pressed={showDeleteButtons}
                                        onClick={() => setShowDeleteButtons(!showDeleteButtons)}
                                        title={
                                            showDeleteButtons
                                                ? 'Stop showing delete buttons on each card'
                                                : 'Reveal delete buttons on each rating card'
                                        }
                                        className="coffee-header-action-btn shrink-0"
                                    >
                                        {showDeleteButtons ? 'Hide deletes' : 'Show deletes'}
                                    </Button>

                                    <Link
                                        to="/admin/clusters"
                                        className={cn(
                                            buttonVariants({ variant: 'outline', size: 'sm' }),
                                            'coffee-header-action-btn inline-flex shrink-0 items-center gap-2 font-medium no-underline'
                                        )}
                                        title="Coffee shop region map and H3 clusters (admin)"
                                        aria-label="Open coffee clusters admin map"
                                    >
                                        <FontAwesomeIcon icon={faMapMarkedAlt} className="h-4 w-4" aria-hidden />
                                        Cluster areas
                                    </Link>

                                    <Link
                                        to="/admin/chains"
                                        className={cn(
                                            buttonVariants({ variant: 'outline', size: 'sm' }),
                                            'coffee-header-action-btn inline-flex shrink-0 items-center gap-2 font-medium no-underline'
                                        )}
                                        title="Coffee chain brands and name rules (admin)"
                                        aria-label="Open chain brands admin"
                                    >
                                        <FontAwesomeIcon icon={faLink} className="h-4 w-4" aria-hidden />
                                        Brands & chains
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="header-controls">
                        <div className="search-control">
                            <FontAwesomeIcon icon={faSearch} className="search-control-icon" aria-hidden />
                            <input
                                type="search"
                                className="search-input"
                                placeholder="Search by city, café, or country..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search coffee ratings by city or coffee shop name"
                            />
                        </div>
                        <div className="sort-controls">
                            <label htmlFor="coffee-sort-select" className="sr-only">
                                How to sort the coffee list
                            </label>
                            <select
                                id="coffee-sort-select"
                                className="sort-select"
                                value={sortBy}
                                onChange={handleSortChange}
                            >
                                <option value="date">Newest first</option>
                                <option value="rating">Highest score first</option>
                                <option value="style">Grouped: hot/cold/style</option>
                                <option value="group-type">Grouped: by coffee type</option>
                            </select>
                            <FontAwesomeIcon icon={faChevronDown} className="sort-icon" />
                        </div>
                    </div>
                </div>

                {/* Submission Status Card - tappable to resume */}
                {showExternalStatus && externalSubmissionData && (
                    <button
                        type="button"
                        onClick={handleResumeFromStatus}
                        className="w-full text-left mb-4 cursor-pointer group"
                    >
                        <CoffeeStatusIndicator
                            status={externalSubmissionData.status}
                            inline
                            message={externalSubmissionData.statusInfo.message}
                            progress={externalSubmissionData.statusInfo.progress}
                            showProgress
                        />
                        <p className="text-xs text-gray-500 mt-2 text-center group-hover:text-gray-700 transition-colors">
                            tap to resume
                        </p>
                    </button>
                )}

                {/* Add Coffee Form Modal */}
                {showAddForm && (
                    <AddCoffeeForm
                        onClose={handleCloseForm}
                        onSuccess={handleAddSuccess}
                        onSubmissionUpdate={handleSubmissionUpdate}
                        autoResume={shouldAutoResume}
                    />
                )}

                {loading && visibleItems === ITEMS_PER_PAGE ? (
                    <div>Loading...</div>
                ) : sortBy === 'style' ? (
                    renderGroupedCoffees()
                ) : sortBy === 'group-type' ? (
                    renderGroupedByType()
                ) : (
                    <div className="coffee-grid">
                        {sortedCoffees.slice(0, visibleItems).map((coffee, index) => (
                            <CoffeeCardWeb
                                key={coffee._id || index}
                                coffeeData={coffee}
                                onDelete={handleCoffeeDeleted}
                                showDeleteButtons={showDeleteButtons}
                            />
                        ))}
                    </div>
                )}
                {visibleItems < sortedCoffees.length && (
                    <div ref={loadMoreRef} className="load-more-trigger">
                        Loading more...
                    </div>
                )}
            </div>
        </ImageCacheProvider>
    );
};

export default CoffeeRatingsPage;
